import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ProfileFieldProps {
  icon: ReactNode;
  /** Tint of the icon tile — the profile's own palette. */
  tone: string;
  label: string;
  /** Null, undefined or blank reads "Not provided" — never a made-up value. */
  value: ReactNode;
  mono?: boolean;
}

/**
 * One labelled fact on My Profile.
 *
 * Every field on every tab is drawn by this, so a field the school has not
 * recorded says so the same way everywhere instead of showing a sample.
 */
export function ProfileField({ icon, tone, label, value, mono = false }: ProfileFieldProps) {
  const isBlank = value === null || value === undefined || value === '';

  return (
    <div className="flex items-start gap-3.5">
      <div
        className={cn(
          'w-10 h-10 rounded-xl border flex items-center justify-center shrink-0',
          tone,
        )}
        aria-hidden
      >
        {icon}
      </div>
      <div className="min-w-0">
        <span className="text-sm text-ink-3 font-bold block">{label}</span>
        {isBlank ? (
          <span className="text-sm font-medium text-ink-3 italic mt-0.5 block">-</span>
        ) : (
          <span
            className={cn(
              'text-base font-bold text-ink mt-0.5 block break-words',
              mono && 'font-mono',
            )}
          >
            {value}
          </span>
        )}
      </div>
    </div>
  );
}
